
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/hooks/use-toast";
import { detectLanguage } from '@/lib/languageUtils';

interface UseEmailTemplateGeneratorProps {
  onSuccess: (content: string) => void;
  onGenerating: (isGenerating: boolean) => void;
  onStreamUpdate?: (content: string) => void;
  onStreamComplete?: (content: string) => void;
}

export const useEmailTemplateGenerator = ({
  onSuccess,
  onGenerating,
  onStreamUpdate,
  onStreamComplete
}: UseEmailTemplateGeneratorProps) => {
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  /**
   * Generate an email template from a concept using our dedicated edge function
   */
  const generateTemplate = useCallback(async (concept: string, companyInfo: any = null): Promise<void> => {
    if (!concept || isGenerating) {
      console.log("Cannot generate template: Empty concept or already generating");
      return;
    }

    try {
      console.log("[EmailTemplateGenerator] Starting template generation for concept:", concept);
      setIsGenerating(true);
      setError(null);
      onGenerating(true);

      // Notify user that generation is starting
      toast({
        title: "Template wird generiert",
        description: "Bitte haben Sie einen Moment Geduld...",
      });
      
      // Detect language for better prompting
      const language = detectLanguage(concept) || 'de';
      
      // Use streaming for better user experience
      console.log("[EmailTemplateGenerator] Using streaming generation");
      await generateTemplateWithStreaming(concept, language, companyInfo);

    } catch (error) {
      console.error("[EmailTemplateGenerator] Error generating template:", error);
      setError(error instanceof Error ? error.message : "Unknown error");
      toast({
        title: "Fehler",
        description: "Die Template-Generierung ist fehlgeschlagen. Bitte versuchen Sie es erneut.",
        variant: "destructive"
      });
    } finally {
      // We don't reset isGenerating here, as that should be handled by the appropriate
      // success or completion callbacks to ensure proper state synchronization
    }
  }, [isGenerating, onGenerating, onSuccess, toast, onStreamUpdate, onStreamComplete]);

  /**
   * Generate template using streaming
   */
  const generateTemplateWithStreaming = async (concept: string, language: string, companyInfo: any = null): Promise<void> => {
    console.log("[EmailTemplateGenerator] Starting streaming template generation");
    let accumulatedContent = '';

    try {
      // Use EventSource for streaming updates
      const params = new URLSearchParams({
        streaming: 'true',
        concept: encodeURIComponent(concept)
      });
      
      const eventSource = new EventSource(`https://qmuqbkpcfslysjbptlnj.supabase.co/functions/v1/generate-template?${params.toString()}`);
      
      // Set up timeout for streaming connection
      const timeoutId = setTimeout(() => {
        console.log("[EmailTemplateGenerator] Streaming timed out, closing connection");
        eventSource.close();
        
        // Try to use accumulated content if we have it
        if (accumulatedContent && isValidHtml(accumulatedContent)) {
          if (onStreamComplete) onStreamComplete(accumulatedContent);
          onSuccess(accumulatedContent);
          setIsGenerating(false);
          onGenerating(false);
          toast({
            title: "Template erstellt",
            description: "Die E-Mail-Vorlage wurde trotz Timeout erfolgreich erstellt."
          });
        } else {
          throw new Error("Template generation timed out. Please try again.");
        }
      }, 60000); // 60 second timeout
      
      // Handle streaming updates
      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'template_update' && data.content) {
            console.log("[EmailTemplateGenerator] Stream update received, content length:", data.content.length);
            accumulatedContent = data.content;
            if (onStreamUpdate) {
              onStreamUpdate(data.content);
            }
          }
          
          if (data.type === 'done') {
            clearTimeout(timeoutId);
            eventSource.close();
            
            const finalContent = data.content || accumulatedContent;
            
            if (finalContent && isValidHtml(finalContent)) {
              console.log("[EmailTemplateGenerator] Streaming complete, valid HTML received");
              
              if (onStreamComplete) {
                onStreamComplete(finalContent);
              }
              
              onSuccess(finalContent);
              setIsGenerating(false);
              onGenerating(false);
              
              toast({
                title: "Template erstellt",
                description: "Die E-Mail-Vorlage wurde erfolgreich erstellt."
              });
            } else {
              fallbackToDirectCall(concept, language, companyInfo);
            }
          }
        } catch (e) {
          console.error("[EmailTemplateGenerator] Error processing stream event:", e);
        }
      };
      
      eventSource.onerror = (err) => {
        console.error("[EmailTemplateGenerator] EventSource error:", err);
        clearTimeout(timeoutId);
        eventSource.close();
        
        // If we have accumulated some content, try to use that
        if (accumulatedContent && isValidHtml(accumulatedContent)) {
          console.log("[EmailTemplateGenerator] Using accumulated content despite error");
          
          if (onStreamComplete) {
            onStreamComplete(accumulatedContent);
          }
          
          onSuccess(accumulatedContent);
          setIsGenerating(false);
          onGenerating(false);
          
          toast({
            title: "Template erstellt",
            description: "Die E-Mail-Vorlage wurde erstellt, aber es könnten Fehler aufgetreten sein."
          });
        } else {
          // Otherwise fallback to direct API call
          fallbackToDirectCall(concept, language, companyInfo);
        }
      };
      
    } catch (error) {
      console.error("[EmailTemplateGenerator] Error in streaming generation:", error);
      // Fallback to direct API call if streaming completely fails
      await fallbackToDirectCall(concept, language, companyInfo);
    }
  };

  /**
   * Last resort fallback - direct API call
   */
  const fallbackToDirectCall = async (concept: string, language: string, companyInfo: any = null): Promise<void> => {
    console.log("[EmailTemplateGenerator] Falling back to direct API call");
    
    try {
      const { data, error: directError } = await supabase.functions.invoke('generate-template', {
        body: {
          concept,
          streaming: false,
          language,
          companyInfo
        }
      });

      if (directError) {
        throw new Error(`Direct API call failed: ${directError.message}`);
      }

      if (data?.content && isValidHtml(data.content)) {
        console.log("[EmailTemplateGenerator] Template generated successfully via fallback");
        
        if (onStreamComplete) {
          onStreamComplete(data.content);
        }
        
        onSuccess(data.content);
        setIsGenerating(false);
        onGenerating(false);
        
        toast({
          title: "Template erstellt",
          description: "Die E-Mail-Vorlage wurde erfolgreich erstellt."
        });
      } else {
        throw new Error("Failed to generate valid HTML template");
      }
    } catch (finalError) {
      console.error("[EmailTemplateGenerator] All template generation attempts failed:", finalError);
      setIsGenerating(false);
      onGenerating(false);
      toast({
        title: "Fehler",
        description: "Die Template-Generierung ist fehlgeschlagen. Bitte versuchen Sie es später erneut.",
        variant: "destructive"
      });
    }
  };

  /**
   * Validates if the content appears to be an HTML template - more lenient version
   */
  const isValidHtml = (content: string): boolean => {
    // More flexible validation
    if (!content || typeof content !== 'string') {
      return false;
    }
    
    // Check if it has some basic HTML structure (more lenient)
    return (
      (content.includes('<!DOCTYPE html>') || 
       content.includes('<html') || 
       content.includes('<body') ||
       (content.includes('<div') && content.includes('</div>')) ||
       content.includes('<table')) &&
      content.length > 100
    );
  };

  return {
    generateTemplate,
    isGenerating,
    error
  };
};

export default useEmailTemplateGenerator;
